import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "../i18n";

/**
 * Desktop hover tooltip listing who reacted to a pill, with a "… see all"
 */
const ReactorTooltip = ({
  anchorEl,
  reaction,
  maxNames,
  onSeeAll,
  onMouseEnter,
  onMouseLeave,
  onRequestClose,
}) => {
  const { t } = useTranslation("feed");
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !anchorEl) return undefined;
    const a = anchorEl.getBoundingClientRect();
    const margin = 8;
    const left = Math.min(
      Math.max(a.left, margin),
      Math.max(window.innerWidth - el.offsetWidth - margin, margin)
    );
    let top = a.top - el.offsetHeight - 6;
    if (top < margin) top = a.bottom + 6;
    setPos({ left, top });
    window.addEventListener("scroll", onRequestClose, true);
    window.addEventListener("resize", onRequestClose);
    return () => {
      window.removeEventListener("scroll", onRequestClose, true);
      window.removeEventListener("resize", onRequestClose);
    };
  }, [anchorEl, reaction, onRequestClose]);

  const names = reaction.reactorNames || [];

  return createPortal(
    <div
      ref={ref}
      className="fixed z-[150] bg-white border border-gray-200 rounded-xl shadow-lg py-2 px-3 max-w-[220px]"
      style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, visibility: "hidden" }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <ul className="space-y-0.5">
        {names.slice(0, maxNames).map((name, i) => (
          <li key={i} className="text-xs text-gray-700 truncate">
            {name}
          </li>
        ))}
      </ul>
      {reaction.count > maxNames && (
        <button
          type="button"
          className="mt-1 text-xs font-medium text-gray-400 hover:text-gray-700 transition-colors"
          onClick={onSeeAll}
        >
          {t("reactions.see_all", { count: reaction.count })}
        </button>
      )}
    </div>,
    document.body
  );
};

export default ReactorTooltip;
